import {
  DETECTION_RUNTIME_DEFAULTS,
  VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
  VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
} from "../generated/visionRuntimeParameters";
import {
  createVisionRuntimeSnapshot,
  visionRuntimeProvenance,
  type VisionRuntimeProvenance,
  type VisionRuntimeSnapshot,
} from "./visualLabService";
import { inferProductionCnn, type CnnWebInferenceResult } from "./cnnWebInferenceService";
import {
  LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
  LIVE_RAW_GEOMETRY_VERIFY_THRESHOLDS,
  meanCornerDistanceRatio,
  type GeometryDetectionIntentMode,
  type GeometryWarmStartExecution,
} from "./liveRawGeometryVerificationService";
import type { MultiLabelGuideIntent } from "../types/liveGeometry";

export type LabelDetectionWorkerStatus =
  | "idle"
  | "starting"
  | "ready"
  | "error";

export type LabelCorner = {
  x: number;
  y: number;
};

export type NormalizedRoi = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type GeometryWarmStartDetectionOptions = {
  mode: GeometryDetectionIntentMode;
  guideIntent: MultiLabelGuideIntent;
  preserveGuideOnFallback: boolean;
  multiLabelGuideActive: boolean;
  fallbackGuideCorners?: LabelCorner[];
  fallbackGuideConfidence?: number;
};

export type LabelDetectionMethod =
  | "grid+orb"
  | "grid"
  | "grid-reconstruction"
  | "orb"
  | "structure"
  | "contour"
  | "hough"
  | "candidate-pool"
  | "cnn-fast-refine"
  | "none";

export type LabelContourExclusionStats = {
  totalContours: number;
  areaTooSmall: number;
  areaTooLarge: number;
  notFourPoints: number;
  notConvex: number;
  rectangularityTooLow: number;
  aspectRatioInvalid: number;
  outerAreaTooSmall: number;
  validCandidates: number;
};

export type LabelCandidateDebug = {
  index: number;
  areaRatio: number;
  rectangularity: number;
  aspectRatio: number;
  score: number;
  eligibleAsOuterLabel: boolean;
  corners: LabelCorner[];
};

export type SpatialColumnDiagnostics = {
  left: number;
  center: number;
  right: number;
};

export type OrbSpatialDiagnostics = {
  gridColumns: number;
  gridRows: number;
  sourceKeypointsByColumn: SpatialColumnDiagnostics;
  targetKeypointsByColumn: SpatialColumnDiagnostics;
  balancedMatchesByColumn: SpatialColumnDiagnostics;
  inliersByColumn: SpatialColumnDiagnostics;
  coveredCells: number;
  totalCells: number;
  cellCoverage: number;
  xCoverage: number;
  yCoverage: number;
  quadrantCoverage: number;
  balanceScore: number;
  spatialConfidence: number;
};

export type OrbDiagnostics = {
  available: boolean;
  phase: string;
  error: string | null;
  sourceKeypoints: number;
  targetKeypoints: number;
  sourceDescriptorRows: number;
  targetDescriptorRows: number;
  rawMatches: number;
  ratioMatches: number;
  geometricMatches: number;
  balancedMatches: number;
  inliers: number;
  usedKnn: boolean;
  usedGeometricPrior: boolean;
  usedSpatialBalancing: boolean;
  confidenceScore: number;
  baseConfidenceScore: number;
  reprojectionError: number | null;
  spatial: OrbSpatialDiagnostics;
  decisionMode: "refinement" | "recovery" | "mapping" | "none";
  cornerShiftRatio: number | null;
  accepted: boolean;
  rejectionReason: string | null;
  preprocessing: "clahe" | "equalizeHist" | "grayscale";
  candidateCorners: LabelCorner[] | null;
  candidateGeometry: GridGeometryDiagnostics;
  internalMinAreaRatio: number | null;
  internalGeometryScore: number | null;
};


export type QuickWarpDiagnostics = {
  checked: boolean;
  accepted: boolean;
  horizontalSupported: number;
  verticalSupported: number;
  averageSupport: number;
  horizontalSupports?: number[];
  verticalSupports?: number[];
  horizontalReferencePositions?: number[];
  verticalReferencePositions?: number[];
  minimumLineRatio?: number;
  reason: string | null;
};

export type TargetGuidanceDiagnostics = {
  available: boolean;
  targetCorners: LabelCorner[] | null;
  selectedCornerDistanceRatio: number | null;
  rescueUsed: boolean;
  rescueMethod: LabelDetectionMethod | null;
};

export type CandidatePoolDiagnostics = {
  index: number;
  source: string;
  method: LabelDetectionMethod;
  corners: LabelCorner[];
  baseScore: number;
  geometry: GridGeometryDiagnostics;
  targetDistanceRatio: number | null;
  referenceFit: QuickWarpDiagnostics;
  referenceScore: number;
  combinedScore: number;
  accepted: boolean;
  selected: boolean;
  rejectionReason: string | null;
};
export type FinalGeometryDiagnostics = {
  checked: boolean;
  accepted: boolean;
  horizontalSupported: number;
  verticalSupported: number;
  averageSupport: number;
  horizontalSupports?: number[];
  verticalSupports?: number[];
  horizontalReferencePositions?: number[];
  verticalReferencePositions?: number[];
  minimumLineRatio?: number;
  reason: string | null;
};

export type GridGeometryDiagnostics = {
  checked: boolean;
  accepted: boolean;
  areaRatio: number | null;
  aspectRatio: number | null;
  geometryScore: number | null;
  convex: boolean | null;
  plausibleBounds: boolean | null;
  reason: string | null;
};

export type GridAxisDiagnostics = {
  available: boolean;
  scale: number | null;
  offset: number | null;
  score: number | null;
  residual: number | null;
  matches: { reference: number; observed: number }[];
};

export type GridHypothesisDiagnostics = {
  index: number;
  horizontalAxis: GridAxisDiagnostics;
  verticalAxis: GridAxisDiagnostics;
  reconstructionCorners: LabelCorner[];
  geometry: GridGeometryDiagnostics;
  targetDistanceRatio: number | null;
  quickWarp: QuickWarpDiagnostics;
  preliminaryScore: number;
  combinedScore: number;
  selected: boolean;
};

export type LabelDetectionVisionSnapshot = Omit<VisionRuntimeProvenance, "source"> & {
  source: "database" | "default" | "override";
};


export type LabelDetectionPhaseTimings = {
  inputToMatMs: number;
  preprocessMs: number;
  gridMs: number;
  orbMs: number;
  structureMs: number;
  contourMs: number;
  houghMs: number;
  candidateEvaluationInclusiveMs: number;
  finalizationMs: number;
  totalMs: number;
};

export type ParallelDetectionCandidateEvidence = {
  schemaVersion: 1;
  contractVersion: "pv14-detection-candidate-evaluation-parallel-v1";
  profileId: "pv14-parallel-detection-candidate-evaluation-v1";
  status: "parallel" | "serial" | "fallback";
  fullFidelityPreserved: true;
  productionInfluenced: boolean;
  requestedWorkers: 2;
  effectiveWorkers: 1 | 2;
  poolWarm: boolean;
  workerStartupMs: number;
  candidateCount: number;
  localTaskCount: number;
  remoteTaskCount: number;
  parallelWallMs: number | null;
  serialEquivalentMs: number | null;
  estimatedSpeedup: number | null;
  overlapMs: number | null;
  overlapRatio: number | null;
  transferMode: "structured-clone-image-data" | "none";
  serialFallbackUsed: boolean;
  reason: string | null;
  error: string | null;
};

export type LabelDetectionResult = {
  found: boolean;
  score: number;
  imageWidth: number;
  imageHeight: number;
  corners: LabelCorner[] | null;
  areaRatio: number | null;
  rectangularity: number | null;
  contourCount: number;
  candidateCount: number;
  processingTimeMs: number;
  phaseTimings?: LabelDetectionPhaseTimings;
  parallelDetectionCandidateEvaluation: ParallelDetectionCandidateEvidence;
  detectionMethod: LabelDetectionMethod;
  houghLineCount: number;
  exclusionStats: LabelContourExclusionStats;
  candidates: LabelCandidateDebug[];

  gridScore: number;
  gridHorizontalLines: number;
  gridVerticalLines: number;
  gridHorizontalPositions: number[];
  gridVerticalPositions: number[];
  gridMatchedHorizontal: number;
  gridMatchedVertical: number;
  homographyInliers: number;
  orbMatches: number;
  orbInliers: number;
  orbInlierRatio: number;
  orbScore: number;
  orbDiagnostics: OrbDiagnostics;
  referenceUsed: boolean;
  gridReconstructionUsed: boolean;
  gridReconstructionScore: number;
  gridReconstructionReason: string | null;
  gridGeometry: GridGeometryDiagnostics;
  gridHomographyCorners: LabelCorner[] | null;
  gridReconstructionCandidateCorners: LabelCorner[] | null;
  gridReconstructionGeometry: GridGeometryDiagnostics;
  gridHorizontalAxis: GridAxisDiagnostics;
  gridVerticalAxis: GridAxisDiagnostics;
  gridHypotheses: GridHypothesisDiagnostics[];
  rejectionChain: string[];
  finalGeometry: FinalGeometryDiagnostics;
  quickWarp: QuickWarpDiagnostics;
  targetGuidance: TargetGuidanceDiagnostics;
  candidatePool: CandidatePoolDiagnostics[];
  geometryWarmStartExecution?: GeometryWarmStartExecution;
  visionSnapshot?: LabelDetectionVisionSnapshot;
};

export type PhotoRoiMappingResult = {
  found: boolean;
  method:
    | "grid+orb"
    | "grid-homography"
    | "orb-homography"
    | "scaled-fallback";
  confidence: number;
  matchCount: number;
  inlierCount: number;
  inlierRatio: number;
  photoCorners: LabelCorner[];
  gridCoarseUsed: boolean;
  previewGridScore: number;
  photoGridScore: number;
  orbDiagnostics: OrbDiagnostics;
};

export type RectifiedLabelResult = {
  imageData: ImageData;
  outputWidth: number;
  outputHeight: number;
};

export interface LabelDetectionWorkerDiagnostic {
  phase: string;
  detail: string | null;
  cvType: string | null;
  matType: string | null;
  elapsedMs: number | null;
}

export type DetectionThresholdOverrides = Record<string, number>;

type WorkerResponse =
  | {
      type: "progress";
      phase: string;
      detail?: string;
      cvType?: string;
      matType?: string;
      elapsedMs?: number;
    }
  | {
      type: "ready";
      elapsedMs: number;
      runtimeCatalogVersion: string;
      runtimeCatalogSha256: string;
    }
  | {
      type: "candidate-runtime-status";
      warm: boolean;
      startupMs: number;
      error: string | null;
    }
  | {
      type: "pong";
    }
  | {
      type: "detection-result";
      requestId: string;
      result: LabelDetectionResult;
    }
  | {
      type: "mapping-result";
      requestId: string;
      result: PhotoRoiMappingResult;
    }
  | {
      type: "rectification-result";
      requestId: string;
      result: RectifiedLabelResult;
    }
  | {
      type: "detection-error" | "mapping-error" | "rectification-error";
      requestId: string;
      message: string;
    }
  | {
      type: "error";
      phase: string;
      message: string;
      stack?: string;
    };

type PendingDetection = {
  resolve: (result: LabelDetectionResult) => void;
  reject: (reason: Error) => void;
  timeout: number;
};

type PendingMapping = {
  resolve: (result: PhotoRoiMappingResult) => void;
  reject: (reason: Error) => void;
  timeout: number;
};

type PendingRectification = {
  resolve: (result: RectifiedLabelResult) => void;
  reject: (reason: Error) => void;
  timeout: number;
};

const OPENCV_URL = "/opencv/opencv-4.9.0.js";
const REFERENCE_URL = "/reference/etikett_blanko_reference.png";
const WORKER_START_TIMEOUT_MS = 30000;
const DETECTION_TIMEOUT_MS = 15000;
const MAPPING_TIMEOUT_MS = 15000;

let worker: Worker | null = null;
let startPromise: Promise<void> | null = null;
let candidateRuntimeWarm = false;
let candidateRuntimeStartupMs = 0;
let candidateRuntimeError: string | null = null;
let requestCounter = 0;
let referencePromise: Promise<ImageData | null> | null = null;

const pendingDetections = new Map<string, PendingDetection>();
const pendingMappings = new Map<string, PendingMapping>();
const pendingRectifications = new Map<string, PendingRectification>();

let lastDiagnostic: LabelDetectionWorkerDiagnostic = {
  phase: "idle",
  detail: null,
  cvType: null,
  matType: null,
  elapsedMs: null,
};

const diagnosticListeners = new Set<
  (diagnostic: LabelDetectionWorkerDiagnostic) => void
>();

function publishDiagnostic(
  diagnostic: LabelDetectionWorkerDiagnostic
): void {
  lastDiagnostic = diagnostic;
  console.info("[OpenCV Worker]", diagnostic);

  for (const listener of diagnosticListeners) {
    listener(diagnostic);
  }
}

export function getLabelDetectionWorkerDiagnostic():
  LabelDetectionWorkerDiagnostic {
  return { ...lastDiagnostic };
}

export function subscribeLabelDetectionWorkerDiagnostic(
  listener: (diagnostic: LabelDetectionWorkerDiagnostic) => void
): () => void {
  diagnosticListeners.add(listener);
  listener({ ...lastDiagnostic });

  return () => {
    diagnosticListeners.delete(listener);
  };
}

function createWorker(): Worker {
  return new Worker(
    new URL("../workers/labelDetectionWorker.ts", import.meta.url)
  );
}

const RUNTIME_GENERATION_ERROR_PREFIX = "RUNTIME_CATALOG_MISMATCH";
const RUNTIME_GENERATION_RECOVERY_KEY = "materialentnahme.vision.runtimeGenerationRecovery.v1";

function scheduleRuntimeGenerationRecovery(): void {
  try {
    if (window.sessionStorage.getItem(RUNTIME_GENERATION_RECOVERY_KEY) === "1") return;
    window.sessionStorage.setItem(RUNTIME_GENERATION_RECOVERY_KEY, "1");
    window.setTimeout(() => window.location.reload(), 120);
  } catch {
    // Ohne sessionStorage bleibt der fail-closed Fehler sichtbar; kein Reload-Loop.
  }
}

function clearRuntimeGenerationRecoveryGuard(): void {
  try {
    window.sessionStorage.removeItem(RUNTIME_GENERATION_RECOVERY_KEY);
  } catch {
    // Best effort.
  }
}

function runtimeGenerationMismatchMessage(detail: string): string {
  return `${RUNTIME_GENERATION_ERROR_PREFIX}: ${detail}. App und OpenCV-Worker stammen nicht aus derselben Build-Generation. Bitte die PWA vollständig schließen und neu öffnen.`;
}

export function getLabelDetectionWorker(): Worker | null {
  return worker;
}

let healthPingPromise: Promise<void> | null = null;

function pingLabelDetectionWorker(timeoutMs: number): Promise<void> {
  const activeWorker = worker;
  if (!activeWorker) {
    return Promise.reject(new Error("OpenCV-Worker ist nicht gestartet."));
  }
  if (healthPingPromise) return healthPingPromise;

  const ping = new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      activeWorker.removeEventListener("message", onMessage);
      activeWorker.removeEventListener("error", onError);
      if (error) reject(error);
      else resolve();
    };
    const onMessage = (event: MessageEvent<WorkerResponse>) => {
      if (event.data?.type === "pong") finish();
    };
    const onError = (event: ErrorEvent) => {
      finish(new Error(event.message || "OpenCV-Worker-Healthcheck fehlgeschlagen."));
    };
    const timeout = window.setTimeout(() => {
      finish(new Error(`OpenCV-Worker antwortet nicht innerhalb von ${timeoutMs} ms.`));
    }, timeoutMs);
    activeWorker.addEventListener("message", onMessage);
    activeWorker.addEventListener("error", onError);
    activeWorker.postMessage({ type: "ping" });
  }).finally(() => {
    if (healthPingPromise === ping) healthPingPromise = null;
  });
  healthPingPromise = ping;
  return ping;
}

/**
 * v0.282.4 / iOS-WebKit Re-Admission:
 * Ein vorhandener Worker ist erst dann warm, wenn sein Event-Loop auf einen
 * expliziten Ping antwortet. Ein von WebKit nach Background/Visibility
 * eingefrorener oder verlorener Worker wird einmal hart recycelt und über den
 * normalen Runtime-Katalog-Handshake neu gestartet.
 */
export async function ensureLabelDetectionWorkerHealthy(timeoutMs = 1500): Promise<{ restarted: boolean }> {
  await startLabelDetectionWorker();
  try {
    await pingLabelDetectionWorker(timeoutMs);
    return { restarted: false };
  } catch {
    stopLabelDetectionWorker();
    await startLabelDetectionWorker();
    await pingLabelDetectionWorker(timeoutMs);
    return { restarted: true };
  }
}

export function isLabelDetectionParallelCandidateRuntimeWarm(): boolean {
  return worker !== null && candidateRuntimeWarm;
}

export function getLabelDetectionParallelCandidateRuntimeStatus(): { warm: boolean; startupMs: number; error: string | null } {
  return { warm: candidateRuntimeWarm, startupMs: candidateRuntimeStartupMs, error: candidateRuntimeError };
}

function rejectAllPending(message: string): void {
  for (const pending of pendingDetections.values()) {
    window.clearTimeout(pending.timeout);
    pending.reject(new Error(message));
  }
  pendingDetections.clear();

  for (const pending of pendingMappings.values()) {
    window.clearTimeout(pending.timeout);
    pending.reject(new Error(message));
  }
  pendingMappings.clear();

  for (const pending of pendingRectifications.values()) {
    window.clearTimeout(pending.timeout);
    pending.reject(new Error(message));
  }
  pendingRectifications.clear();
}

async function loadReferenceImageData(): Promise<ImageData | null> {
  if (referencePromise) {
    return referencePromise;
  }

  const currentReferencePromise = (async () => {
    try {
      const response = await fetch(REFERENCE_URL, {
        cache: "force-cache",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      try {
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;

        const context = canvas.getContext("2d", {
          willReadFrequently: true,
        });

        if (!context) {
          throw new Error("Canvas-Kontext nicht verfügbar.");
        }

        context.drawImage(bitmap, 0, 0);
        return context.getImageData(0, 0, bitmap.width, bitmap.height);
      } finally {
        bitmap.close();
      }
    } catch (error) {
      console.warn(
        "[OpenCV Worker] Referenzbild konnte nicht geladen werden. Grid-Erkennung läuft für diesen Versuch ohne ORB-Fine-Matching; der nächste Scan versucht den Load erneut.",
        error
      );
      return null;
    }
  })();
  referencePromise = currentReferencePromise;
  const result = await currentReferencePromise;
  if (result === null && referencePromise === currentReferencePromise) {
    referencePromise = null;
  }
  return result;
}

export function startLabelDetectionWorker(): Promise<void> {
  if (worker) {
    return Promise.resolve();
  }

  if (startPromise) {
    return startPromise;
  }

  publishDiagnostic({
    phase: "creating-worker",
    detail: "Worker wird erzeugt.",
    cvType: null,
    matType: null,
    elapsedMs: 0,
  });

  startPromise = new Promise<void>((resolve, reject) => {
    const newWorker = createWorker();
    let startupFinished = false;
    let lastPhase = "creating-worker";
    let lastDetail = "Worker wird erzeugt.";

    const startupTimeout = window.setTimeout(() => {
      failStartup(
        new Error(
          `OpenCV-Worker Timeout. Letzte Phase: "${lastPhase}". Detail: "${lastDetail}".`
        )
      );
    }, WORKER_START_TIMEOUT_MS);

    function cleanupStartup(): void {
      window.clearTimeout(startupTimeout);
    }

    function failStartup(error: Error): void {
      if (startupFinished) {
        return;
      }

      startupFinished = true;
      cleanupStartup();

      publishDiagnostic({
        phase: "error",
        detail: error.message,
        cvType: null,
        matType: null,
        elapsedMs: null,
      });

      newWorker.terminate();
      worker = null;
      startPromise = null;
      candidateRuntimeWarm = false;
      candidateRuntimeError = error.message;
      rejectAllPending("OpenCV-Worker wurde beendet.");
      reject(error);
    }

    function failRuntime(error: Error): void {
      if (!startupFinished || worker !== newWorker) {
        return;
      }
      publishDiagnostic({
        phase: "runtime-error",
        detail: error.message,
        cvType: null,
        matType: null,
        elapsedMs: null,
      });
      // Ein Worker-ErrorEvent bzw. ein request-unabhängiger fataler Workerfehler
      // bedeutet, dass die Worker-Instanz nicht mehr vertrauenswürdig ist. Vollständig
      // verwerfen, Pending Requests ablehnen und beim nächsten Scan sauber neu starten.
      newWorker.terminate();
      worker = null;
      startPromise = null;
      candidateRuntimeWarm = false;
      candidateRuntimeError = error.message;
      rejectAllPending("OpenCV-Worker ist unerwartet ausgefallen und wird beim nächsten Versuch neu gestartet.");
    }

    const handleError = (event: ErrorEvent): void => {
      if (!startupFinished) {
        failStartup(
          new Error(event.message || "Unbekannter Worker-Fehler.")
        );
        return;
      }

      failRuntime(new Error(event.message || "Unbekannter Worker-Fehler."));
    };

    const handleMessage = (
      event: MessageEvent<WorkerResponse>
    ): void => {
      const message = event.data;

      if (message.type === "progress") {
        lastPhase = message.phase;
        lastDetail = message.detail ?? "";

        publishDiagnostic({
          phase: message.phase,
          detail: message.detail ?? null,
          cvType: message.cvType ?? null,
          matType: message.matType ?? null,
          elapsedMs: message.elapsedMs ?? null,
        });
        return;
      }

      if (message.type === "candidate-runtime-status") {
        candidateRuntimeWarm = message.warm;
        candidateRuntimeStartupMs = message.startupMs;
        candidateRuntimeError = message.error;
        return;
      }

      if (message.type === "ready") {
        if (startupFinished) {
          return;
        }
        if (
          message.runtimeCatalogVersion !== VISION_RUNTIME_PARAMETER_CATALOG_VERSION
          || message.runtimeCatalogSha256 !== VISION_RUNTIME_PARAMETER_CATALOG_SHA256
        ) {
          scheduleRuntimeGenerationRecovery();
          failStartup(new Error(runtimeGenerationMismatchMessage(
            `Main ${VISION_RUNTIME_PARAMETER_CATALOG_VERSION}/${VISION_RUNTIME_PARAMETER_CATALOG_SHA256.slice(0, 12)} · Worker ${message.runtimeCatalogVersion || "–"}/${message.runtimeCatalogSha256?.slice(0, 12) || "–"}`
          )));
          return;
        }

        clearRuntimeGenerationRecoveryGuard();
        startupFinished = true;
        cleanupStartup();
        worker = newWorker;
        startPromise = Promise.resolve();

        publishDiagnostic({
          phase: "ready",
          detail: `OpenCV ist im Worker bereit · Runtime-Katalog ${message.runtimeCatalogSha256.slice(0, 12)}.`,
          cvType: "object",
          matType: "function",
          elapsedMs: message.elapsedMs,
        });

        resolve();
        return;
      }

      if (message.type === "detection-result") {
        const pending = pendingDetections.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingDetections.delete(message.requestId);
        pending.resolve(message.result);
        return;
      }

      if (message.type === "mapping-result") {
        const pending = pendingMappings.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingMappings.delete(message.requestId);
        pending.resolve(message.result);
        return;
      }

      if (message.type === "rectification-result") {
        const pending = pendingRectifications.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingRectifications.delete(message.requestId);
        pending.resolve(message.result);
        return;
      }

      if (message.type === "detection-error") {
        const pending = pendingDetections.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingDetections.delete(message.requestId);
        pending.reject(new Error(message.message));
        return;
      }

      if (message.type === "mapping-error") {
        const pending = pendingMappings.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingMappings.delete(message.requestId);
        pending.reject(new Error(message.message));
        return;
      }

      if (message.type === "rectification-error") {
        const pending = pendingRectifications.get(message.requestId);
        if (!pending) {
          return;
        }

        window.clearTimeout(pending.timeout);
        pendingRectifications.delete(message.requestId);
        pending.reject(new Error(message.message));
        return;
      }

      if (message.type === "error") {
        if (!startupFinished) {
          if (message.phase === "runtime-generation") scheduleRuntimeGenerationRecovery();
          failStartup(
            new Error(
              `OpenCV-Worker (${message.phase}): ${message.message}`
            )
          );
          return;
        }

        failRuntime(new Error(`OpenCV-Worker (${message.phase}): ${message.message}`));
      }
    };

    newWorker.addEventListener("message", handleMessage);
    newWorker.addEventListener("error", handleError);
    newWorker.postMessage({
      type: "init",
      opencvUrl: OPENCV_URL,
      defaultThresholds: { ...DETECTION_RUNTIME_DEFAULTS },
      runtimeCatalogVersion: VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
      runtimeCatalogSha256: VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
    });
  });

  return startPromise;
}

function nextRequestId(prefix: string): string {
  requestCounter += 1;
  return `${prefix}-${Date.now()}-${requestCounter}`;
}

export async function detectLabelInImageData(
  imageData: ImageData,
  thresholdOverrides?: DetectionThresholdOverrides,
  targetCorners?: LabelCorner[],
  targetConfidence?: number,
  runtimeSnapshot?: VisionRuntimeSnapshot,
  geometryWarmStartOptions?: GeometryWarmStartDetectionOptions,
  parallelCandidateEvaluationAllowed = false,
  onRequestDispatched?: () => void,
): Promise<LabelDetectionResult> {
  await startLabelDetectionWorker();

  if (!worker) {
    throw new Error("OpenCV-Worker ist nicht verfügbar.");
  }

  const referenceImageData = await loadReferenceImageData();
  // Ein produktiver Scan friert die Vision-Runtime genau einmal ein. Mapping, Detection
  // und OCR verwenden danach denselben Snapshot, auch wenn der lokale Production-Cache
  // im Hintergrund bereits auf ein neueres Bundle aktualisiert wird.
  const resolvedRuntimeSnapshot = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
  const thresholds = thresholdOverrides ?? resolvedRuntimeSnapshot.detectionParameters;
  const initialTargetCorners = targetCorners?.length === 4 ? targetCorners : undefined;
  const initialTargetConfidence = targetConfidence;
  const guideTargetCorners = geometryWarmStartOptions?.fallbackGuideCorners?.length === 4
    ? geometryWarmStartOptions.fallbackGuideCorners
    : initialTargetCorners;
  const guideTargetConfidence = geometryWarmStartOptions?.fallbackGuideConfidence ?? initialTargetConfidence;
  let effectiveTargetCorners = initialTargetCorners;
  let effectiveTargetConfidence = initialTargetConfidence;
  let fastPath = geometryWarmStartOptions?.mode === "TRUSTED_SEED" && Boolean(initialTargetCorners);
  let fastPathSource: "SR23_TRUSTED_SEED" | "PRODUCTION_CNN" | "NONE" = fastPath ? "SR23_TRUSTED_SEED" : "NONE";
  let cnnInference: CnnWebInferenceResult | null = null;
  let cnnGuideDistanceRatio: number | null = null;
  let cnnFastPathSuppressedByGuide = false;
  const productionBundle = thresholdOverrides ? null : resolvedRuntimeSnapshot.productionBundle;

  // SR23: Ein TRUSTED LIVE→RAW-Seed wird zuerst mit dem bereits vorhandenen
  // OpenCV-Subpixel-/Geometry-/Raster-Fast-Path verifiziert. Bei GUIDED bleibt
  // die volle Production-Detection maßgeblich. Ein Production-CNN-Fast-Path darf
  // bei Multi-Label-Szenen den Preview-Guide nur verwenden, wenn beide Quads
  // räumlich hinreichend übereinstimmen; sonst läuft die Vollsuche mit Guide.
  if (!fastPath && !thresholdOverrides && resolvedRuntimeSnapshot.cnn.enabled && resolvedRuntimeSnapshot.hybrid.fastPathEnabled && productionBundle?.runtime_ready) {
    try {
      const cnn = await inferProductionCnn(imageData, productionBundle);
      cnnInference = cnn;
      if (cnn.confidence >= productionBundle.hybrid.confidence_threshold) {
        if (geometryWarmStartOptions?.multiLabelGuideActive && guideTargetCorners) {
          cnnGuideDistanceRatio = meanCornerDistanceRatio(guideTargetCorners, cnn.corners);
          const agreesWithGuide = cnnGuideDistanceRatio !== null
            && cnnGuideDistanceRatio <= LIVE_RAW_GEOMETRY_VERIFY_THRESHOLDS.multiLabelGuideMaxMeanCornerDistanceRatio;
          if (agreesWithGuide) {
            effectiveTargetCorners = cnn.corners;
            effectiveTargetConfidence = cnn.confidence;
            fastPath = true;
            fastPathSource = "PRODUCTION_CNN";
          } else {
            cnnFastPathSuppressedByGuide = true;
          }
        } else {
          effectiveTargetCorners = cnn.corners;
          effectiveTargetConfidence = cnn.confidence;
          fastPath = true;
          fastPathSource = "PRODUCTION_CNN";
        }
      }
    } catch {
      // Offline-first safety: fehlendes/inkompatibles CNN darf den Scan nie blockieren.
      // Die vorhandene OpenCV-Pipeline läuft unverändert weiter.
    }
  }
  const runDetectionRequest = (
    requestedTargetCorners: LabelCorner[] | undefined,
    requestedTargetConfidence: number | undefined,
    requestedFastPath: boolean,
  ) => {
    const requestId = nextRequestId(requestedFastPath ? "label-fast" : "label");
    return new Promise<LabelDetectionResult>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        pendingDetections.delete(requestId);
        reject(
          new Error(
            "Die Etiketterkennung wurde nicht innerhalb von 15 Sekunden abgeschlossen."
          )
        );
      }, DETECTION_TIMEOUT_MS);

      pendingDetections.set(requestId, { resolve, reject, timeout });
      worker?.postMessage({
        type: "detect-label",
        requestId,
        imageData,
        referenceImageData,
        thresholds,
        targetCorners: requestedTargetCorners,
        targetConfidence: requestedTargetConfidence,
        fastPath: requestedFastPath,
        parallelCandidateEvaluationAllowed,
      });
      onRequestDispatched?.();
    });
  };

  let result = await runDetectionRequest(effectiveTargetCorners, effectiveTargetConfidence, fastPath);
  const firstPassMethod = result.detectionMethod;
  const fastSeedAttempted = fastPathSource === "SR23_TRUSTED_SEED";
  const fastSeedAccepted = fastSeedAttempted && result.found && result.finalGeometry.accepted;
  let fastPathFallbackUsed = false;
  let guidePreservedOnFallback = false;

  // Candidate-Integrity + SR23 Forced Fallback: jeder abgelehnte Shortcut fällt
  // auf die vollständige bestehende OpenCV-Pipeline zurück. Für SR23 bleibt der
  // Preview-/Multi-Label-Guide dabei erhalten; ein Seed darf die Vollsuche nie
  // unterdrücken oder eine unbestätigte Geometrie erzwingen.
  if (fastPath && !result.found) {
    fastPathFallbackUsed = true;
    const preserveGuide = geometryWarmStartOptions?.preserveGuideOnFallback === true && Boolean(guideTargetCorners);
    guidePreservedOnFallback = preserveGuide;
    result = await runDetectionRequest(
      preserveGuide ? guideTargetCorners : undefined,
      preserveGuide ? guideTargetConfidence : undefined,
      false,
    );
  }

  const geometryWarmStartExecution: GeometryWarmStartExecution | undefined = geometryWarmStartOptions
    ? {
        contract_version: "geometry-warmstart-execution-v1",
        policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
        requested_mode: geometryWarmStartOptions.mode,
        guide_intent: geometryWarmStartOptions.guideIntent,
        fast_seed_attempted: fastSeedAttempted,
        fast_seed_accepted: fastSeedAccepted,
        forced_full_detection_fallback: fastSeedAttempted && fastPathFallbackUsed,
        guide_preserved_on_fallback: guidePreservedOnFallback,
        multi_label_guide_active: geometryWarmStartOptions.multiLabelGuideActive,
        cnn_guide_distance_ratio: cnnGuideDistanceRatio,
        cnn_fast_path_suppressed_by_guide: cnnFastPathSuppressedByGuide,
        first_pass_method: firstPassMethod,
        final_method: result.detectionMethod,
        reason: fastSeedAccepted
          ? "TRUSTED_SEED_VERIFIED"
          : fastSeedAttempted && fastPathFallbackUsed
            ? "TRUSTED_SEED_REJECTED_FORCED_FULL_DETECTION"
            : cnnFastPathSuppressedByGuide
              ? "CNN_DISAGREED_WITH_MULTI_LABEL_GUIDE_FULL_DETECTION"
              : geometryWarmStartOptions.mode === "GUIDED_FULL_DETECTION"
                ? "GUIDED_FULL_DETECTION"
                : "FULL_DETECTION",
      }
    : undefined;

  return {
    ...result,
    ...(geometryWarmStartExecution ? { geometryWarmStartExecution } : {}),
    visionSnapshot: {
      ...visionRuntimeProvenance(resolvedRuntimeSnapshot, {
        fastPathAttempted: fastPath,
        fastPathFallbackUsed,
        cnnInference: cnnInference ? {
          confidence: cnnInference.confidence,
          perImageUncertainty: cnnInference.perImageUncertainty,
          ttaMaxCornerDelta: cnnInference.ttaMaxCornerDelta,
          ttaPasses: cnnInference.ttaPasses,
          expectedCornerError: cnnInference.expectedCornerError,
          calibrationVersion: cnnInference.calibrationVersion,
          calibrationSource: cnnInference.calibrationSource,
          geometryScore: cnnInference.geometryScore,
          runtimeMs: cnnInference.runtimeMs,
        } : null,
      }),
      source: thresholdOverrides ? "override" : resolvedRuntimeSnapshot.source,
      detectionParameters: { ...(thresholds ?? {}) },
      ...(thresholdOverrides ? {
        cnn: { ...resolvedRuntimeSnapshot.cnn, enabled: false },
        hybrid: { ...resolvedRuntimeSnapshot.hybrid, fastPathEnabled: false },
      } : {}),
    },
  };
}

export async function mapPreviewRoiToPhoto(
  previewImageData: ImageData,
  photoImageData: ImageData,
  previewRoi: NormalizedRoi,
  runtimeSnapshot?: VisionRuntimeSnapshot
): Promise<PhotoRoiMappingResult> {
  await startLabelDetectionWorker();

  if (!worker) {
    throw new Error("OpenCV-Worker ist nicht verfügbar.");
  }

  const resolvedRuntimeSnapshot = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
  const requestId = nextRequestId("mapping");

  return new Promise<PhotoRoiMappingResult>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      pendingMappings.delete(requestId);
      reject(
        new Error(
          "Die Zuordnung zwischen Livebild und Foto wurde nicht rechtzeitig abgeschlossen."
        )
      );
    }, MAPPING_TIMEOUT_MS);

    pendingMappings.set(requestId, {
      resolve,
      reject,
      timeout,
    });

    worker?.postMessage({
      type: "map-preview-photo",
      requestId,
      previewImageData,
      photoImageData,
      previewRoi,
      thresholds: resolvedRuntimeSnapshot.detectionParameters,
    });
  });
}

export type RectificationSourceMode = "cropped-analysis" | "raw-photo";

export async function rectifyLabelImageData(
  imageData: ImageData,
  corners: LabelCorner[],
  outputWidth = 1800,
  outputHeight = 450,
  options?: { sourceMode?: RectificationSourceMode; timeoutMs?: number }
): Promise<RectifiedLabelResult> {
  await startLabelDetectionWorker();

  if (!worker) {
    throw new Error("OpenCV-Worker ist nicht verfügbar.");
  }

  if (corners.length !== 4) {
    throw new Error("Für die Perspektivkorrektur werden vier Etikettecken benötigt.");
  }

  const requestId = nextRequestId("rectify");

  return new Promise<RectifiedLabelResult>((resolve, reject) => {
    const timeoutMs = Math.max(1000, options?.timeoutMs ?? DETECTION_TIMEOUT_MS);
    const requestWorker = worker;
    const timeout = window.setTimeout(() => {
      pendingRectifications.delete(requestId);
      // Ein Timeout bedeutet, dass die aktuelle Worker-Instanz für weitere
      // Rectifications nicht mehr vertrauenswürdig ist. Besonders WebKit kann
      // nach Background/Resume einen Worker-Handle behalten, dessen Event-Loop
      // nicht mehr liefert. Genau diese Instanz deshalb vor dem nächsten Versuch
      // verwerfen, statt noch einen zweiten 15-s-Request hineinzuschicken.
      if (requestWorker !== null && worker === requestWorker) {
        stopLabelDetectionWorker();
      }
      reject(
        new Error(
          `Die Perspektivkorrektur wurde nicht innerhalb von ${Math.round(timeoutMs / 1000)} Sekunden abgeschlossen; der OpenCV-Worker wurde verworfen.`
        )
      );
    }, timeoutMs);

    pendingRectifications.set(requestId, {
      resolve,
      reject,
      timeout,
    });

    worker?.postMessage({
      type: "rectify-label",
      requestId,
      imageData,
      corners,
      outputWidth,
      outputHeight,
      sourceMode: options?.sourceMode ?? "cropped-analysis",
    });
  });
}

export function stopLabelDetectionWorker(): void {
  rejectAllPending("OpenCV-Worker wurde beendet.");

  if (worker) {
    worker.terminate();
    worker = null;
  }

  startPromise = null;
  healthPingPromise = null;
  candidateRuntimeWarm = false;
  candidateRuntimeStartupMs = 0;
  candidateRuntimeError = null;

  publishDiagnostic({
    phase: "stopped",
    detail: "Worker wurde beendet.",
    cvType: null,
    matType: null,
    elapsedMs: null,
  });
}
